import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FileSignature, RefreshCw, CheckCircle2, AlertOctagon, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

// Crash log del flusso firma (request-otp / verify-otp / sign-contract).
// Post-mortem bug NameError 07/07/2026: i 500 in produzione devono
// emergere qui prima che li segnali un cliente.
export const SignatureErrorsWidget = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [unresolved, setUnresolved] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [resolving, setResolving] = useState(null);

  const fetchErrors = useCallback(async (includeResolved = showResolved) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/system/signature-errors`, { params: { include_resolved: includeResolved } });
      setItems(r.data.items || []);
      setUnresolved(r.data.unresolved_count || 0);
    } catch {
      toast.error("Errore caricamento crash log firma");
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const markResolved = async (id) => {
    setResolving(id);
    try {
      await axios.post(`${API}/admin/system/signature-errors/${id}/resolve`);
      toast.success("Errore segnato come risolto");
      fetchErrors();
    } catch {
      toast.error("Impossibile segnare come risolto");
    } finally {
      setResolving(null);
    }
  };

  return (
    <div className="bg-[#111] border border-gray-700 rounded-2xl p-5" data-testid="signature-errors-widget">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <FileSignature size={18} className="text-lojack-red" />
          Crash Log — Flusso Firma
          {unresolved > 0 && (
            <span className="px-2 py-0.5 bg-red-500/15 border border-red-500/40 text-red-300 text-xs rounded-full" data-testid="signature-errors-badge">
              {unresolved} da risolvere
            </span>
          )}
        </h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => { setShowResolved(e.target.checked); }}
              className="accent-red-600"
              data-testid="signature-errors-show-resolved"
            />
            Mostra risolti
          </label>
          <button
            onClick={() => fetchErrors()}
            disabled={loading}
            className="p-1.5 text-gray-400 hover:text-white border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors"
            data-testid="signature-errors-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
      <p className="text-gray-500 text-xs mb-4">
        Errori 500 su request-otp / verify-otp / sign-contract, registrati automaticamente dal backend.
      </p>

      {items.length === 0 ? (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/8 border border-emerald-500/30 rounded-xl" data-testid="signature-errors-empty">
          <CheckCircle2 size={16} className="text-emerald-300" />
          <span className="text-emerald-300 text-sm">Nessun errore di firma{showResolved ? "" : " aperto"} — tutto regolare</span>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="signature-errors-list">
          {items.map((e) => (
            <div key={e.id} className={`rounded-xl border p-3 ${e.resolved ? "border-gray-700 bg-[#0A0A0A] opacity-60" : "border-red-500/30 bg-red-500/5"}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!e.resolved && <AlertOctagon size={13} className="text-red-400 shrink-0" />}
                    <span className="px-1.5 py-0.5 bg-gray-800 border border-gray-600 text-gray-300 text-[10px] font-mono rounded">{e.endpoint}</span>
                    <span className="text-gray-500 text-xs">{fmtTime(e.created_at)}</span>
                    {e.resolved && (
                      <span className="text-emerald-400 text-[10px] uppercase tracking-wide">Risolto {e.resolved_by ? `· ${e.resolved_by}` : ""}</span>
                    )}
                  </div>
                  <p className="text-red-200 text-xs mt-1 font-mono break-all">{e.error}</p>
                  <p className="text-gray-500 text-[11px] mt-0.5">Cliente: <span className="font-mono">{e.customer_id}</span></p>
                  {e.traceback && (
                    <details className="mt-1">
                      <summary className="text-gray-500 text-[11px] cursor-pointer hover:text-gray-300">Traceback</summary>
                      <pre className="mt-1 p-2 bg-black/60 border border-gray-800 rounded-lg text-[10px] text-gray-400 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">{e.traceback}</pre>
                    </details>
                  )}
                </div>
                {!e.resolved && (
                  <button
                    onClick={() => markResolved(e.id)}
                    disabled={resolving === e.id}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 text-xs rounded-lg transition-colors"
                    data-testid={`signature-error-resolve-${e.id}`}
                  >
                    <Check size={12} /> Segna risolto
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
